import type { ApiResponse, PaginatedResponse } from '@/shared/api/types/api-response'
import type {
  SurveyAnswerCreateRequest,
  SurveyAnswerDTO,
  SurveyAnswersListParams,
  SurveyAnswerUpdateRequest,
  SurveyQuestionCreateRequest,
  SurveyQuestionDTO,
  SurveyQuestionsListParams,
  SurveyQuestionUpdateRequest,
  SurveyResponseDetailDTO,
  SurveyResponsesListParams,
  SurveyResponseSummaryDTO,
  SurveyVersionCreateRequest,
  SurveyVersionDTO,
  SurveyVersionsListParams,
  SurveyVersionUpdateRequest,
} from '@/shared/api/types/survey-types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createSurveyAnswer,
  createSurveyQuestion,
  createSurveyVersion,
  deleteSurveyQuestion,
  deleteSurveyVersion,
  getSurveyQuestionById,
  getSurveyVersionById,
  listSurveyAnswers,
  listSurveyQuestions,
  listSurveyResponses,
  listSurveyResponsesByUser,
  listSurveyVersions,
  updateSurveyAnswer,
  updateSurveyQuestion,
  updateSurveyVersion,
} from '@/shared/api/services/survey-service'

const SURVEY_QUERY_KEYS = {
  all: ['surveys'] as const,
  versionsList: (tenantId: string, params: SurveyVersionsListParams) =>
    [...SURVEY_QUERY_KEYS.all, 'versions', 'list', tenantId, params] as const,
  versionDetail: (versionId: string) =>
    [...SURVEY_QUERY_KEYS.all, 'versions', 'detail', versionId] as const,
  questionsList: (versionId: string, params: SurveyQuestionsListParams) =>
    [...SURVEY_QUERY_KEYS.all, 'questions', 'list', versionId, params] as const,
  questionDetail: (questionId: string) =>
    [...SURVEY_QUERY_KEYS.all, 'questions', 'detail', questionId] as const,
  answersList: (questionId: string, params: SurveyAnswersListParams) =>
    [...SURVEY_QUERY_KEYS.all, 'answers', 'list', questionId, params] as const,
  responsesList: (tenantId: string, params: SurveyResponsesListParams) =>
    [...SURVEY_QUERY_KEYS.all, 'responses', 'list', tenantId, params] as const,
  responsesByUser: (tenantId: string, userId: string, params: SurveyResponsesListParams) =>
    [...SURVEY_QUERY_KEYS.all, 'responses', 'detail', tenantId, userId, params] as const,
}

export function useSurveyVersionsQuery(
  tenantId: string,
  params: SurveyVersionsListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<PaginatedResponse<SurveyVersionDTO>, Error>({
    queryKey: SURVEY_QUERY_KEYS.versionsList(tenantId, params),
    queryFn: async () => listSurveyVersions(params),
    enabled: !!tenantId && (options?.enabled ?? true),
  })
}

export function useSurveyVersionByIdQuery(versionId: string, options?: { enabled?: boolean }) {
  return useQuery<ApiResponse<SurveyVersionDTO>, Error>({
    queryKey: SURVEY_QUERY_KEYS.versionDetail(versionId),
    queryFn: async () => getSurveyVersionById(versionId),
    enabled: !!versionId && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useSurveyQuestionsQuery(
  tenantId: string,
  versionId: string,
  params: SurveyQuestionsListParams,
  options?: { enabled?: boolean; languageCode?: string },
) {
  const languageKey = options?.languageCode ?? ''
  const effectiveParams = {
    ...params,
    ...(options?.languageCode ? { languageCode: options.languageCode } : {}),
  }
  return useQuery<PaginatedResponse<SurveyQuestionDTO>, Error>({
    queryKey: [...SURVEY_QUERY_KEYS.questionsList(versionId, params), languageKey] as const,
    queryFn: async () => listSurveyQuestions(versionId, effectiveParams),
    enabled: !!tenantId && !!versionId && (options?.enabled ?? true),
  })
}

export function useSurveyQuestionByIdQuery(questionId: string, options?: { enabled?: boolean }) {
  return useQuery<ApiResponse<SurveyQuestionDTO>, Error>({
    queryKey: SURVEY_QUERY_KEYS.questionDetail(questionId),
    queryFn: async () => getSurveyQuestionById(questionId),
    enabled: !!questionId && (options?.enabled ?? true),
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export function useSurveyAnswersQuery(
  tenantId: string,
  questionId: string,
  params: SurveyAnswersListParams,
  options?: { enabled?: boolean; languageCode?: string },
) {
  const languageKey = options?.languageCode ?? ''
  const effectiveParams = {
    ...params,
    ...(options?.languageCode ? { languageCode: options.languageCode } : {}),
  }
  return useQuery<PaginatedResponse<SurveyAnswerDTO>, Error>({
    queryKey: [...SURVEY_QUERY_KEYS.answersList(questionId, params), languageKey] as const,
    queryFn: async () => listSurveyAnswers(questionId, effectiveParams),
    enabled: !!tenantId && !!questionId && (options?.enabled ?? true),
  })
}

export function useSurveyResponsesQuery(
  tenantId: string,
  params: SurveyResponsesListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<PaginatedResponse<SurveyResponseSummaryDTO>, Error>({
    queryKey: SURVEY_QUERY_KEYS.responsesList(tenantId, params),
    queryFn: async () => listSurveyResponses(tenantId, params),
    enabled: options?.enabled ?? true,
  })
}

export function useSurveyResponsesByUserQuery(
  tenantId: string,
  userId: string,
  params: SurveyResponsesListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<PaginatedResponse<SurveyResponseDetailDTO>, Error>({
    queryKey: SURVEY_QUERY_KEYS.responsesByUser(tenantId, userId, params),
    queryFn: async () => listSurveyResponsesByUser(tenantId, userId, params),
    enabled: !!userId && (options?.enabled ?? true),
  })
}

export function useCreateSurveyVersionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: SurveyVersionCreateRequest) => createSurveyVersion(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...SURVEY_QUERY_KEYS.all, 'versions', 'list'],
      })
    },
  })
}

export function useUpdateSurveyVersionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      versionId,
      payload,
    }: {
      versionId: string
      payload: SurveyVersionUpdateRequest
    }) => updateSurveyVersion(versionId, payload),
    onSuccess: async (_data, { versionId }) => {
      await queryClient.invalidateQueries({
        queryKey: [...SURVEY_QUERY_KEYS.all, 'versions', 'list'],
      })
      await queryClient.invalidateQueries({ queryKey: SURVEY_QUERY_KEYS.versionDetail(versionId) })
    },
  })
}

export function useDeleteSurveyVersionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (versionId: string) => deleteSurveyVersion(versionId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...SURVEY_QUERY_KEYS.all, 'versions', 'list'],
      })
    },
  })
}

export function useCreateSurveyQuestionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      versionId,
      payload,
    }: {
      versionId: string
      payload: SurveyQuestionCreateRequest
    }) => createSurveyQuestion(versionId, payload),
    onSuccess: async (_data, { versionId }) => {
      await queryClient.invalidateQueries({
        queryKey: [...SURVEY_QUERY_KEYS.all, 'questions', 'list', versionId],
      })
    },
  })
}

export function useUpdateSurveyQuestionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      questionId,
      payload,
    }: {
      questionId: string
      payload: SurveyQuestionUpdateRequest
    }) => updateSurveyQuestion(questionId, payload),
    onSuccess: async (response, { questionId }) => {
      await queryClient.invalidateQueries({
        queryKey: SURVEY_QUERY_KEYS.questionDetail(questionId),
      })
      const versionId = response.data.versionId
      await queryClient.invalidateQueries({
        queryKey: [...SURVEY_QUERY_KEYS.all, 'questions', 'list', versionId],
      })
    },
  })
}

export function useDeleteSurveyQuestionMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { questionId: string; versionId: string }) =>
      deleteSurveyQuestion(vars.questionId),
    onSuccess: async (_data, { questionId, versionId }) => {
      queryClient.removeQueries({ queryKey: SURVEY_QUERY_KEYS.questionDetail(questionId) })
      await queryClient.invalidateQueries({
        queryKey: [...SURVEY_QUERY_KEYS.all, 'questions', 'list', versionId],
        refetchType: 'all',
      })
    },
  })
}

export function useCreateSurveyAnswerMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      questionId,
      payload,
    }: {
      questionId: string
      payload: SurveyAnswerCreateRequest
    }) => createSurveyAnswer(questionId, payload),
    onSuccess: async (_data, { questionId }) => {
      await queryClient.invalidateQueries({
        queryKey: [...SURVEY_QUERY_KEYS.all, 'answers', 'list', questionId],
      })
    },
  })
}

export function useUpdateSurveyAnswerMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (vars: {
      answerId: string
      questionId: string
      payload: SurveyAnswerUpdateRequest
    }) => updateSurveyAnswer(vars.answerId, vars.payload),
    onSuccess: async (_data, { questionId }) => {
      await queryClient.invalidateQueries({
        queryKey: [...SURVEY_QUERY_KEYS.all, 'answers', 'list', questionId],
      })
    },
  })
}
